import React from "react";
import { Container, Row, Col } from "reactstrap";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <React.Fragment>
      <section className="section" id="privacy-policy">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9}>
              <h2>Privacy Policy</h2>
              <p className="text-muted">Last updated: August 13, 2026</p>

              <p className="text-muted mt-4">
                Quran411.org (&quot;Quran411&quot;, &quot;we&quot;, &quot;us&quot;
                or &quot;our&quot;) provides free access to the Holy Quran,
                including translations, transliterations and audio recitations,
                through our website{" "}
                <Link to="https://www.quran411.org">www.quran411.org</Link> and
                our Android and iOS mobile applications (together, the
                &quot;Service&quot;). This Privacy Policy explains what
                information we collect, how we use it, and the choices you
                have. It applies to the website and to the mobile apps.
              </p>

              <h4 className="mt-5">1. Information We Collect</h4>
              <p className="text-muted">
                Quran411 does not require you to create an account, sign in, or
                provide any personal information to use the Service. We collect
                only the following limited information:
              </p>
              <ul className="text-muted">
                <li className="mt-2">
                  <b>Contact form information.</b> If you contact us through
                  our contact form, we collect the name, email address and
                  message you choose to provide. We use this information only
                  to respond to your enquiry.
                </li>
                <li className="mt-2">
                  <b>Usage and analytics data.</b> We use Google Analytics to
                  understand how the Service is used. Google Analytics
                  automatically collects information such as the pages you
                  visit, the time and duration of your visit, your approximate
                  location (city or country level), device type, operating
                  system, browser type and anonymised network information. This
                  data is aggregated and does not directly identify you.
                </li>
                <li className="mt-2">
                  <b>Technical log data.</b> When you load pages or stream
                  audio recitations, our hosting and content-delivery servers
                  may automatically record standard technical logs, such as
                  your IP address, request time and the file requested. These
                  logs are used only to operate, secure and troubleshoot the
                  Service.
                </li>
              </ul>
              <p className="text-muted">
                We do not collect sensitive personal information, we do not
                track your reading or listening habits against your identity,
                and we do not use your data for advertising.
              </p>

              <h4 className="mt-5">2. How We Use Information</h4>
              <ul className="text-muted">
                <li className="mt-2">To provide, operate and maintain the Service.</li>
                <li className="mt-2">To respond to messages you send us.</li>
                <li className="mt-2">
                  To understand overall usage of the Service so we can improve
                  content, performance and reliability.
                </li>
                <li className="mt-2">
                  To detect, prevent and address technical or security issues.
                </li>
              </ul>
              <p className="text-muted">
                We do not sell, rent or trade your personal information to
                anyone, and we do not use it for marketing.
              </p>

              <h4 className="mt-5">3. Cookies and Similar Technologies</h4>
              <p className="text-muted">
                The Service uses a small number of cookies and similar
                technologies set by Google Analytics to measure usage as
                described above. You can block or delete cookies through your
                browser settings, and you can opt out of Google Analytics
                across all websites by installing the{" "}
                <Link to="https://tools.google.com/dlpage/gaoptout">
                  Google Analytics opt-out browser add-on
                </Link>
                . The Service remains fully usable with cookies disabled.
              </p>

              <h4 className="mt-5">4. Third-Party Services</h4>
              <p className="text-muted">
                We rely on a small number of third-party service providers to
                run the Service. These providers process data only as needed to
                provide their services to us:
              </p>
              <ul className="text-muted">
                <li className="mt-2">
                  <b>Google Analytics</b> (Google LLC) — usage analytics. See
                  the{" "}
                  <Link to="https://policies.google.com/privacy">
                    Google Privacy Policy
                  </Link>
                  .
                </li>
                <li className="mt-2">
                  <b>Formspree</b> — processes submissions from our contact
                  form. See the{" "}
                  <Link to="https://formspree.io/legal/privacy-policy/">
                    Formspree Privacy Policy
                  </Link>
                  .
                </li>
                <li className="mt-2">
                  <b>Hosting and content-delivery providers</b> — serve the
                  website, API and audio recitation files.
                </li>
              </ul>
              <p className="text-muted">
                This Privacy Policy does not cover third-party websites that we
                link to. We encourage you to review the privacy policies of any
                external sites you visit.
              </p>

              <h4 className="mt-5">5. Data Retention</h4>
              <p className="text-muted">
                Contact form messages are kept only as long as needed to handle
                your enquiry. Aggregated analytics data is retained according
                to Google Analytics&apos; standard retention settings.
                Technical server logs are kept for a short period for security
                and troubleshooting purposes and are then deleted or rotated.
              </p>

              <h4 className="mt-5">6. Children&apos;s Privacy</h4>
              <p className="text-muted">
                The Service is suitable for all ages and does not knowingly
                collect personal information from anyone, including children
                under 13. If you believe a child has provided us with personal
                information through the contact form, please contact us and we
                will delete it.
              </p>

              <h4 className="mt-5">7. Security</h4>
              <p className="text-muted">
                We take reasonable technical and organisational measures to
                protect the limited information we handle, including serving
                the Service over HTTPS. However, no method of transmission or
                storage is completely secure, and we cannot guarantee absolute
                security.
              </p>

              <h4 className="mt-5">8. International Transfers</h4>
              <p className="text-muted">
                The Service is available worldwide, and the third-party
                providers listed above may process data on servers located in
                other countries. Where this happens, the data is protected by
                the safeguards described in those providers&apos; own privacy
                policies.
              </p>

              <h4 className="mt-5">9. Your Rights and Choices</h4>
              <p className="text-muted">
                Depending on where you live, you may have rights to access,
                correct or delete personal information we hold about you (for
                most users, this is at most a contact form message). To
                exercise any of these rights, contact us using the details
                below and we will respond as soon as reasonably possible. You
                can also use the Service without providing any personal
                information at all.
              </p>

              <h4 className="mt-5">10. Changes to This Policy</h4>
              <p className="text-muted">
                We may update this Privacy Policy from time to time. When we
                do, we will post the updated version on this page and revise
                the &quot;Last updated&quot; date above. Continued use of the
                Service after changes are posted means you accept the updated
                policy.
              </p>

              <h4 className="mt-5">11. Contact Us</h4>
              <p className="text-muted">
                If you have any questions about this Privacy Policy or how your
                information is handled, please reach out through our{" "}
                <Link to="/contact-us">contact page</Link>.
              </p>
            </Col>
          </Row>
        </Container>
      </section>
    </React.Fragment>
  );
};

export default PrivacyPolicy;
